import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { DomainsCircle } from "src/pages/reports/fsci/v1/graphics/domains_circle";
import { FSCIBrandedV1Report } from "src/pages/reports/fsci/v1/types";
import { isNotNil } from "src/utils/type_guards";

import { meta } from "./meta";

/*
 * Types
 */

export type LearningOrientationProps = {
    readonly report: FSCIBrandedV1Report;
};

/*
 * Main
 */

export const LearningOrientation = (props: LearningOrientationProps): React.ReactElement => {
    const { report } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="learningOrientation" inline />

                <PageTitle>Teaching &amp; Learning</PageTitle>
                <Body>
                    Schools are of course known as sites of learning for students. But if school cultures are to
                    flourish, schools and educators themselves must also engage in learning. In fact, a school culture
                    in which educators are committed to ongoing learning and improvement is linked to flourishing not
                    only for the school and educators, but also for students.
                </Body>
                <Body>
                    FSCI data analysis identified constructs related to <em>Learning Orientation</em> for five of the
                    seven survey groups/ (alumni; teachers; leaders/ administrators; support staff; and board members),
                    as follows.
                </Body>
                <Body>
                    <em>
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FSCI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                R.keys(report.value.results.raw.learningOrientation),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle>{m.title}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        data={[
                                            {
                                                label: report.value.year,
                                                value: report.value.results.raw.learningOrientation[construct] * 100,
                                            },
                                            {
                                                label: "FSCI Benchmark",
                                                value:
                                                    report.value.results.benchmarks.learningOrientation[construct] *
                                                    100,
                                            },
                                        ]}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
